import { computed } from 'mobx'
import { task } from 'mobx-task'
import { switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type Job } from '@/features/jobs/Job'
import type { RequestedSupportingItem } from '@/features/jobs/info-gathering/RequestedSupportingItem'
import type { PersonalInfoSupportingItemInput } from './PersonalInfoSupportingItemInput'
import {
  formatFullName,
  formatProfileDate,
  maskPersonalTaxpayerIdentificationNumber,
} from '@/features/jobs/info-gathering/info-gathering-formatters'
import { type IndividualProfileStore } from './IndividualProfileStore'

/**
 * The personal profile the client provided for a job.
 */
export class PersonalProfileViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Loads the personal profile.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._fetch(signal)
  })

  constructor(
    private readonly _job: Job,
    readonly item: RequestedSupportingItem<PersonalInfoSupportingItemInput>,
    private readonly _individualProfileStore: IndividualProfileStore,
  ) {
    // Re-fetches the profile when another one is provided.
    this._lifecycle.manage(
      toStream(() => this.item.input?.provided).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            // A failed refresh keeps showing the previously loaded value.
            this._fetch(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * The provided personal profile version.
   */
  @computed get profile() {
    const provided = this.item.input?.provided
    if (!provided?.profileId || !provided.profileVersion) {
      return null
    }

    return this._individualProfileStore.getPersonalProfile(
      provided.profileId,
      provided.profileVersion,
    )
  }

  /**
   * Whether the provided profile has been fetched; editing before then would
   * start from a blank profile and overwrite it.
   */
  @computed get isLoaded() {
    return !this.item.completed || !!this.profile
  }

  /**
   * The taxpayer's full name.
   */
  @computed get fullName() {
    return this.profile ? formatFullName(this.profile.personalInfo) : ''
  }

  /**
   * The taxpayer's formatted date of birth.
   */
  @computed get dateOfBirth() {
    return formatProfileDate(this.profile?.personalInfo.dateOfBirth ?? null)
  }

  /**
   * The taxpayer's masked personal taxpayer identification number.
   */
  @computed get maskedPersonalTaxpayerIdentificationNumber() {
    return maskPersonalTaxpayerIdentificationNumber(
      this.profile?.personalInfo.personalTaxpayerIdentificationNumber ?? '',
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The section renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _fetch(signal: AbortSignal) {
    if (this.item.completed) {
      await this._individualProfileStore.fetchPersonalProfileVersionForJob(
        this._job.id,
        signal,
      )
    }
  }
}
